/**
 * useChatUnread - Singleton store for unread chat message counts per order.
 *
 * Listens to Socket.IO `chat:message` events and tracks unread counts.
 * Shared across all components via useSyncExternalStore.
 * Call clearOrder(orderId) when the manager opens an order detail view.
 */

import { useEffect, useCallback, useSyncExternalStore } from 'react';
import { useSocketIO } from '../providers/SocketIOProvider';
import type { ChatMessageEvent } from '../providers/SocketIOProvider';

// ============================================================================
// External Store (singleton)
// ============================================================================

/** orderId -> unread count */
const unreadMap = new Map<string, number>();

let storeListeners = new Set<() => void>();
let storeSnapshot = { unreadMap, version: 0 };

function emitChange() {
  storeSnapshot = { unreadMap, version: storeSnapshot.version + 1 };
  storeListeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  storeListeners.add(listener);
  return () => storeListeners.delete(listener);
}

function getSnapshot() {
  return storeSnapshot;
}

// ============================================================================
// Event Handler
// ============================================================================

function handleChatMessage(event: ChatMessageEvent) {
  // Get current user ID to skip own messages
  try {
    const auth = JSON.parse(localStorage.getItem('auth') || '{}');
    if (auth?.id === event.sender?.id) return;
  } catch { /* proceed — count the message */ }

  const current = unreadMap.get(event.orderId) || 0;
  unreadMap.set(event.orderId, current + 1);
  emitChange();
}

// ============================================================================
// Hook
// ============================================================================

export function useChatUnread() {
  const { onChatMessage } = useSocketIO();

  // Register listener once
  useEffect(() => {
    return onChatMessage(handleChatMessage);
  }, [onChatMessage]);

  const store = useSyncExternalStore(subscribe, getSnapshot);

  const getUnreadCount = useCallback(
    (orderId: string): number => {
      return store.unreadMap.get(orderId) || 0;
    },
    [store],
  );

  const clearOrder = useCallback((orderId: string) => {
    if (unreadMap.has(orderId)) {
      unreadMap.delete(orderId);
      emitChange();
    }
  }, []);

  return { getUnreadCount, clearOrder, unreadMap: store.unreadMap };
}
